/**
 * **标题页** —— 菜单的根，也是唯一一块"退无可退"的屏幕。
 *
 * 版面按 `docs/ui-design.md` §5「把界面当版面排」：左边是**标题艺术字**、
 * 右边是排成一列的印章按钮，底下一条低对比的机读行。
 *
 * ## 标题字不是这儿画的
 *
 * 它是 `card/art/title.ts` 的 `titleWordmarkFace()` —— 和牌面、牌背
 * **走同一条 spec 管线**（`spec.ts` → `painter.ts`），所以自动继承了色板纪律
 * 和调优面板。这里只负责把它摆上去、给它一个尺寸，一行画法都不重复。
 *
 * ⚠️ 用的是 `titleWordmarkFace()`（**只有文字**）而不是 `titleFace()`：
 * 后者自带一条 `BASE.raised` 的印张带把字托住，而这一屏后面跑的正是
 * **活着的标量场背景** —— 再叠一块不透明底就等于把这一屏最值钱的东西盖死。
 *
 * ## ⚠️ 这一屏**不建游戏界面**
 *
 * 评审这一轮要的是菜单（标题/汇合/房间/设置）—— 四个页面都已经接上真东西了。
 * 所以「本地对局」是**明确禁用**的，而不是一个点了没反应的按钮 ——
 * 后者会让人以为坏了。
 */

import { Container, Graphics } from 'pixi.js';
import { paintFace, titleWordmarkFace } from '../../render/card/art';
import { TITLE_DESIGN } from '../../render/card/art/tune';
import { createColumn, richLabel, pageChrome, stampButton, TRACKING, uiPad, INK } from '../../render/ui';
import { displayNickname } from '../settings';
import { bi, t } from '../../i18n';
import type { Screen, ScreenContext } from '../screen';
import { OVERLAY, SCREEN } from './ids';

/** 标题字的宽度（逻辑像素）。高度按设计空间的比例推出来，所以它永远不会变形 */
const WORDMARK_WIDTH = 600;
const WORDMARK_HEIGHT = (WORDMARK_WIDTH * TITLE_DESIGN.height) / TITLE_DESIGN.width;

const WORDMARK_X = 72;
const WORDMARK_Y = 146;
const MENU_X = 764;
const MENU_WIDTH = 344;

export function createTitleScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-title';
  let column = createColumn(1);

  const build = (): void => {
    const settings = ctx.settings.get();
    const scale = settings.uiScale;
    const { width, height } = ctx.bounds;

    column = createColumn(scale);
    container.addChild(column.container);
    column.addRaw(
      pageChrome({ scale, width, height, header: bi('title.header'), page: 'P.01' }),
    );

    // ---------------- 标题艺术字（只有文字，没有印张带） ----------------
    // 直接走牌面那条管线：`spec` → `paintFace`
    const wordmark = paintFace(titleWordmarkFace(), {
      width: uiPad(WORDMARK_WIDTH, scale),
      height: uiPad(WORDMARK_HEIGHT, scale),
    });
    wordmark.position.set(uiPad(WORDMARK_X, scale), uiPad(WORDMARK_Y, scale));
    column.addRaw(wordmark);

    // ---------------- 主菜单 ----------------
    const menu = createColumn(scale, 14);
    menu.container.position.set(uiPad(MENU_X, scale), uiPad(238, scale));
    column.addRaw(menu.container);

    menu.add(
      stampButton({
        label: t('title.netplay'),
        scale,
        variant: 'primary',
        width: uiPad(MENU_WIDTH, scale),
        onPress: () => ctx.stack.push(SCREEN.meetup),
      }),
    );

    // ⚠️ 热座只在 dev build 里出现（评审：「令热座模式在 prod build 隐藏」）。
    //    和下面的调试页走同一条编译期摇树：生产构建里 `import.meta.env.DEV` 是
    //    常量 `false`，整段连同字符串一起消失，玩家看不到这条入口。
    if (import.meta.env.DEV) {
      menu.add(
        stampButton({
          label: t('title.hotseat'),
          scale,
          variant: 'secondary',
          width: uiPad(MENU_WIDTH, scale),
          onPress: () => ctx.stack.push(SCREEN.hotseat),
        }),
      );
    }
    menu.add(
      stampButton({
        label: t('common.settings'),
        scale,
        variant: 'quiet',
        width: uiPad(MENU_WIDTH, scale),
        onPress: () => ctx.stack.openOverlay(OVERLAY.settings),
      }),
    );
    // 更新日志页：入口就排在鸣谢按钮上方（用户指定位置）。内容是数据，见 `changelog.ts`
    menu.add(
      stampButton({
        label: t('common.changelog'),
        scale,
        variant: 'quiet',
        width: uiPad(MENU_WIDTH, scale),
        onPress: () => ctx.stack.push(SCREEN.changelog),
      }),
    );
    // 鸣谢页：设置之下、调试页之上（评审指定的位置）。两档 build 都显示 ——
    // 出处与许可是**产品的一部分**，不该只给开发者看。
    menu.add(
      stampButton({
        label: t('common.credit'),
        scale,
        variant: 'quiet',
        width: uiPad(MENU_WIDTH, scale),
        onPress: () => ctx.stack.push(SCREEN.credit),
      }),
    );

    // ⚠️ **只在 dev build 里出现**（评审：「production build 里仍然有测试页按钮…
    //    让它们只出现在 dev build」）。`import.meta.env.DEV` 在生产构建里是
    //    编译期常量 `false`，所以整段（连同那个字符串）都被摇掉了 ——
    //    产物里连 "debug" 这个词都不会有
    if (import.meta.env.DEV) {
      menu.add(
        stampButton({
          label: t('title.debug'),
          scale,
          variant: 'quiet',
          width: uiPad(MENU_WIDTH, scale),
          onPress: () => {
            // 平台调用（换页），不是界面 —— 和 `transfer.ts` 的下载同一类
            window.location.href = '/debug/index.html';
          },
        }),
      );
    }

    // ---------------- 页脚：左版本、右昵称 ----------------
    // 这些原来是右边一个"档案 / COLOPHON"面板；评审说没必要，收成一行。
    // ⚠️ 昵称放**最右边**（评审要求），版本/提交留在左边 ——
    // 两者对称地贴住版面的两个外角，中间空着，读起来是页脚而不是一条信息流。
    const footerY = height - uiPad(48, scale);

    const buildLine = richLabel({
      // ⚠️ 协议层用**完整** commit 比对，页脚只显示缩略 7 位（保持原样）
      text: `v${ctx.build.version}   ·   ${ctx.build.commit.slice(0, 7)}`,
      scale,
      size: 10,
      fill: INK.textDim,
      role: 'mono',
      tracking: TRACKING.wide,
    });
    buildLine.position.set(uiPad(WORDMARK_X + 4, scale), footerY);
    buildLine.alpha = 0.7;
    column.addRaw(buildLine);

    // ★ dev 产物：在 hash 右边挂一枚 DEV 徽章。生产构建里 `channel` 是编译期常量
    //   `'prod'`，整段（连同 'DEV' 这个词）都被摇掉。
    if (ctx.build.channel === 'dev') {
      const badgeText = richLabel({
        text: 'DEV',
        scale,
        size: 8,
        fill: INK.onKnockout,
        role: 'mono',
        bold: true,
        tracking: TRACKING.wide,
      });
      const badgePadX = uiPad(4, scale);
      const badgePadY = uiPad(2, scale);
      const badge = new Container();
      badge.addChild(
        new Graphics()
          .rect(0, 0, badgeText.width + badgePadX * 2, badgeText.height + badgePadY * 2)
          .fill({ color: INK.accent }),
        badgeText,
      );
      badgeText.position.set(badgePadX, badgePadY);
      badge.position.set(
        buildLine.x + buildLine.width + uiPad(8, scale),
        footerY + uiPad(1, scale),
      );
      column.addRaw(badge);
    }

    const playerLine = richLabel({
      text: displayNickname(settings),
      scale,
      size: 10,
      fill: INK.textDim,
      role: 'mono',
      tracking: TRACKING.wide,
    });
    // 右对齐：锚点设在右边，位置贴着版面的右外角
    playerLine.anchor.set(1, 0);
    playerLine.position.set(width - uiPad(WORDMARK_X + 4, scale), footerY);
    playerLine.alpha = 0.7;
    column.addRaw(playerLine);
  };

  build();

  return {
    container,
    rebuild() {
      column.dispose();
      container.removeChildren();
      build();
    },
    onKey(event) {
      // 根屏幕上 ESC 无处可去，明确吃掉，免得继续往外传
      return event.key === 'Escape';
    },
    destroy() {
      column.dispose();
      container.destroy({ children: true });
    },
  };
}
